"use client";

import { useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import {
  MobileBottomToolProvider,
  type MobileBottomToolItem,
} from "@/components/mobile-bottom-tools";
import { MyLeftExtrasProvider } from "./MyLeftExtrasContext";
import {
  MY_DASHBOARD_HOME,
  MY_DASHBOARD_HOME_ICON,
  MY_DASHBOARD_HOME_LABEL,
  MY_DASHBOARD_TITLE,
  MY_NAV_ITEMS,
  isMyNavItemActive,
} from "./my-nav";

const MOBILE_NAV_LABELS: Record<string, string> = {
  [MY_DASHBOARD_HOME]: "대시보드",
  "/my/study-progress": "진행상황",
  "/my/schedule": "일정",
  "/my/error-notes": "오답노트",
  "/my/badges": "도전과제",
  "/my/notes": "필기",
  "/my/comments": "댓글",
};

function getMobileNavLabel(href: string, label: string) {
  return MOBILE_NAV_LABELS[href] ?? label;
}

/**
 * /my 통합 대시보드 공유 레이아웃 (JCG-2073).
 *
 * 사용자 메뉴 항목을 한 화면에서 전환할 수 있게 좌측 탭 네비를 모든 /my 라우트에 공유한다.
 * App Router 레이아웃이라 탭 간 이동은 소프트 내비(전체 페이지 전환 없음)이고, 직접 URL 진입도
 * 그대로 동작한다(딥링크).
 *
 * - 데스크탑 3컬럼 custom 셸(`inShell`): 좌측 사이드바 슬롯(customLeftSlot)에 탭 네비를 portal.
 *   레이아웃이 셸 children 으로 상주하므로 탭을 옮겨도 네비가 언마운트되지 않는다.
 * - 모바일/태블릿(<1024px, 셸 밖): 공통 하단 도구 Dock("내 메뉴")으로 탭 네비를 제공한다.
 *
 * 인증 가드는 각 페이지(ProfileRouteShell / error-notes)가 담당한다 — 오답노트는 익명 접근을
 * 허용하므로 레이아웃에서 일괄 게이트하지 않는다.
 */
export default function MyLayoutClient({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? "";
  const router = useRouter();
  const { inShell, customLeftSlot } = useArticleShellSlots();
  const homeActive = pathname === MY_DASHBOARD_HOME;
  const mobileTools = useMemo<MobileBottomToolItem[]>(
    () => [
      {
        id: MY_DASHBOARD_HOME,
        kind: "view",
        label: getMobileNavLabel(
          MY_DASHBOARD_HOME,
          MY_DASHBOARD_HOME_LABEL,
        ),
        ariaLabel: `${MY_DASHBOARD_HOME_LABEL} 페이지로 이동`,
        icon: MY_DASHBOARD_HOME_ICON,
        order: 0,
        selected: homeActive,
        onSelect: () => router.push(MY_DASHBOARD_HOME),
      },
      ...MY_NAV_ITEMS.map((item, index) => ({
        id: item.href,
        kind: "view" as const,
        label: getMobileNavLabel(item.href, item.label),
        ariaLabel: `${item.label} 페이지로 이동`,
        icon: item.icon,
        order: index + 1,
        selected: isMyNavItemActive(pathname, item.href),
        onSelect: () => router.push(item.href),
      })),
    ],
    [homeActive, pathname, router],
  );

  // 좌측 사이드바 추가 슬롯(outlet): nav 아래 빈 div 노드를 잡아 페이지(예: error-notes)가
  // 자기 컨트롤을 portal 할 수 있게 한다(MyLeftExtrasContext). ref-콜백 setState 라 마운트 시
  // 1회만 갱신(ArticleShell 슬롯 패턴과 동일, 렌더 루프 아님).
  const [extrasSlot, setExtrasSlot] = useState<HTMLElement | null>(null);

  // 데스크탑 셸 좌측 사이드바: 세로 탭 네비
  const desktopNav = (
    <nav aria-label="내 메뉴" className="space-y-1">
      <p className="mb-1.5 px-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {MY_DASHBOARD_TITLE}
      </p>
      <NavLink
        href={MY_DASHBOARD_HOME}
        icon={MY_DASHBOARD_HOME_ICON}
        label={MY_DASHBOARD_HOME_LABEL}
        active={homeActive}
      />
      <div className="my-1.5 h-px bg-border" />
      {MY_NAV_ITEMS.map((item) => (
        <NavLink
          key={item.href}
          href={item.href}
          icon={item.icon}
          label={item.label}
          active={isMyNavItemActive(pathname, item.href)}
        />
      ))}
    </nav>
  );

  if (inShell) {
    return (
      <MyLeftExtrasProvider value={{ extrasSlot }}>
        {children}
        {customLeftSlot &&
          createPortal(
            <>
              {desktopNav}
              {/* 페이지별 좌측 컨트롤(예: error-notes 오답노트 보기/필터/팁)이 portal 되는
                  outlet. 단일 portal 안에 nav 다음으로 그려 DOM 순서를 고정한다(nav 위·아래 extras).
                  비어 있으면(다른 /my 탭) 보이지 않는다. */}
              <div ref={setExtrasSlot} className="mt-3 empty:mt-0" />
            </>,
            customLeftSlot,
          )}
      </MyLeftExtrasProvider>
    );
  }

  // 모바일/태블릿(<1024px): 공통 하단 도구 Dock으로 탭 네비를 제공한다.
  return (
    <MyLeftExtrasProvider value={{ extrasSlot: null }}>
      <MobileBottomToolProvider tools={mobileTools} dockLabel="내 메뉴">
        {children}
      </MobileBottomToolProvider>
    </MyLeftExtrasProvider>
  );
}

/** 데스크탑 셸 좌측 세로 탭 링크. */
function NavLink({
  href,
  icon: Icon,
  label,
  active,
}: {
  href: string;
  icon: LucideIcon;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
        active
          ? "bg-amber-50 font-semibold text-amber-700 dark:bg-amber-950 dark:text-amber-300"
          : "text-foreground/80 hover:bg-accent hover:text-foreground",
      )}
    >
      <Icon
        className={cn(
          "h-[18px] w-[18px] shrink-0",
          active ? "text-amber-500" : "text-muted-foreground",
        )}
        aria-hidden
      />
      <span className="truncate">{label}</span>
    </Link>
  );
}
